// identity-crypto-browser.js
// Encryption primitives for protecting the browser identity's private
// key at rest — two paths, both producing the same on-disk shape
// (AES-256-GCM ciphertext + IV), differing only in how the AES key
// itself is derived:
//
//   - Passphrase: PBKDF2-SHA256 (600,000 iterations — current OWASP
//     guidance for PBKDF2-SHA256 as of this writing) over a per-record
//     random salt. Universal support, no special hardware — but the
//     passphrase itself is the weak point, and it's something to
//     remember and type.
//   - WebAuthn PRF: the AES key is derived (via HKDF) from a secret a
//     hardware-backed authenticator returns only after a successful
//     biometric/platform check — the actual secret never leaves that
//     hardware, and there's nothing to remember or type. See
//     webauthn-prf-browser.js for the ceremony itself; this file only
//     handles turning whatever secret either path produces into an
//     actual AES key and doing the encrypt/decrypt.
//
// Both paths are exercised by tests/browser-identity-crypto-test.mjs
// using Node's native Web Crypto (confirmed genuine, not polyfilled —
// see that test's header). What can't be tested without a real
// authenticator is the WebAuthn ceremony itself (webauthn-prf-browser.js) —
// this file's job stops at "given a secret, do correct AES-GCM", which
// is fully real and fully verified regardless of where that secret came
// from.

const PBKDF2_ITERATIONS = 600_000

async function deriveKeyFromPassphrase (passphrase, salt) {
  const baseKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey']
  )
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  )
}

/**
 * Turn a raw PRF output (32 bytes from the authenticator, see
 * webauthn-prf-browser.js) into a proper AES-GCM key via HKDF — post-
 * processing the raw secret rather than using it directly, per
 * Yubico's own guidance for this exact pattern.
 */
async function deriveKeyFromPrfSecret (prfSecretBytes) {
  const baseKey = await crypto.subtle.importKey('raw', prfSecretBytes, 'HKDF', false, ['deriveKey'])
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(0), info: new TextEncoder().encode('station-identity-key-v1') },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  )
}

function b64 (bytes) {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)))
}

function unb64 (str) {
  return new Uint8Array(atob(str).split('').map((c) => c.charCodeAt(0)))
}

/** @param {Uint8Array} plaintextBytes @param {string} passphrase */
export async function encryptWithPassphrase (plaintextBytes, passphrase) {
  const salt = crypto.getRandomValues(new Uint8Array(16))
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const key = await deriveKeyFromPassphrase(passphrase, salt)
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintextBytes)
  return { kdf: 'pbkdf2', iterations: PBKDF2_ITERATIONS, salt: b64(salt), iv: b64(iv), ciphertext: b64(ciphertext) }
}

/** @param {object} record - as returned by encryptWithPassphrase @param {string} passphrase */
export async function decryptWithPassphrase (record, passphrase) {
  const salt = unb64(record.salt)
  const iv = unb64(record.iv)
  const key = await deriveKeyFromPassphrase(passphrase, salt)
  try {
    const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, unb64(record.ciphertext))
    return new Uint8Array(plaintext)
  } catch {
    // AES-GCM's auth tag check failing is indistinguishable from any
    // other decrypt error at the Web Crypto API level — but in this
    // context it overwhelmingly means one thing: wrong passphrase.
    throw new Error('Incorrect passphrase')
  }
}

/** @param {Uint8Array} plaintextBytes @param {Uint8Array} prfSecretBytes */
export async function encryptWithPrfSecret (plaintextBytes, prfSecretBytes) {
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const key = await deriveKeyFromPrfSecret(prfSecretBytes)
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintextBytes)
  return { kdf: 'webauthn-prf', iv: b64(iv), ciphertext: b64(ciphertext) }
}

/** @param {object} record - as returned by encryptWithPrfSecret @param {Uint8Array} prfSecretBytes */
export async function decryptWithPrfSecret (record, prfSecretBytes) {
  const iv = unb64(record.iv)
  const key = await deriveKeyFromPrfSecret(prfSecretBytes)
  try {
    const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, unb64(record.ciphertext))
    return new Uint8Array(plaintext)
  } catch {
    // Should only happen if the wrong credential/authenticator produced
    // a different PRF secret than the one this record was encrypted
    // with — e.g. a different device, or the platform authenticator
    // was reset.
    throw new Error('Could not unlock with this authenticator')
  }
}
